import { useTranslations } from '@aikoa/i18n/react'
import { View } from 'react-native'
import Icon from '~/components/Icon'
import { FeedbackRequestAcknowledgement } from '~/components/feedback/FeedbackRequestAcknowledgement'
import { ViewTitle } from '~/components/layout/Sections'
import { Body } from '~/components/typography/Content'
import { Heading3 } from '~/components/typography/Titles'
import { Link } from '~/components/ui/text'
import { ToggleButton } from '~/components/ui/toggle-button'
import { InboundEmailView } from '~/components/user/realtors/InboundEmailView'

export type FeedbackRequestCreationMethod = 'manual' | 'inbound-email' | 'contact-support'

export function FeedbackRequestCreationMethodPhase({
  selectedMethod,
  onSelect,
}: {
  selectedMethod?: FeedbackRequestCreationMethod
  onSelect: (method: FeedbackRequestCreationMethod) => void
}) {
  const { t } = useTranslations('feedback')

  const creationMethods: {
    id: FeedbackRequestCreationMethod
    icon: 'edit' | 'mail' | 'help-circle'
    title: string
    description: string
  }[] = [
    {
      id: 'manual',
      icon: 'edit',
      title: t(($) => $.requestFlow.creationMethod.methods.manual.title),
      description: t(($) => $.requestFlow.creationMethod.methods.manual.description),
    },
    {
      id: 'inbound-email',
      icon: 'mail',
      title: t(($) => $.requestFlow.creationMethod.methods.inboundEmail.title),
      description: t(($) => $.requestFlow.creationMethod.methods.inboundEmail.description),
    },
    {
      id: 'contact-support',
      icon: 'help-circle',
      title: t(($) => $.requestFlow.creationMethod.methods.contactSupport.title),
      description: t(($) => $.requestFlow.creationMethod.methods.contactSupport.description),
    },
  ]

  return (
    <View className="flex-1 gap-6">
      <View className="gap-3">
        <ViewTitle>{t(($) => $.requestFlow.creationMethod.title)}</ViewTitle>
        <Body className="text-pretty text-lg/relaxed">
          {t(($) => $.requestFlow.creationMethod.description)}
        </Body>
      </View>

      <View className="gap-2">
        {creationMethods.map((creationMethod) => (
          <ToggleButton
            key={creationMethod.id}
            testID={`feedback-request-creation-method-${creationMethod.id}`}
            value={selectedMethod === creationMethod.id}
            onValueChange={() => onSelect(creationMethod.id)}>
            <View className="flex-row items-center gap-4">
              <Icon
                name={creationMethod.icon}
                library="feather"
                size={22}
                className="mt-0.5 shrink-0 self-start text-primary"
              />
              <View className="flex-1">
                <Heading3>{creationMethod.title}</Heading3>
                <Body>{creationMethod.description}</Body>
              </View>
            </View>
          </ToggleButton>
        ))}
      </View>
    </View>
  )
}

export function FeedbackRequestInboundEmailPhase({
  onSaveToContactsPress,
}: {
  onSaveToContactsPress: () => void
}) {
  const { t } = useTranslations('feedback')

  return (
    <View className="flex-1 gap-6">
      <View className="gap-3">
        <ViewTitle>{t(($) => $.requestFlow.inboundEmailPhase.title)}</ViewTitle>
        <Body>{t(($) => $.requestFlow.inboundEmailPhase.description)}</Body>
        <Body>{t(($) => $.requestFlow.inboundEmailPhase.instructions)}</Body>
      </View>
      <InboundEmailView showInstructions={false} onSaveToContactsPress={onSaveToContactsPress} />
      <FeedbackRequestAcknowledgement className="mt-auto pb-20" />
    </View>
  )
}

export function FeedbackRequestSupportPhase() {
  const { t } = useTranslations(['feedback', 'common'])
  const supportEmail = t(($) => $.common.contactEmail)

  return (
    <View className="flex-1 gap-6">
      <View className="gap-3">
        <ViewTitle>{t(($) => $.requestFlow.supportPhase.title)}</ViewTitle>
        <Body className="text-pretty text-lg/relaxed">
          {t(($) => $.requestFlow.supportPhase.description)}
        </Body>
        <Body className="text-pretty text-sm/relaxed text-primary">
          {t(($) => $.requestFlow.supportPhase.instructions)}
        </Body>
      </View>

      <View className="gap-3 rounded-xl border border-muted bg-card p-5">
        <Link
          className="font-semibold text-lg text-primary underline"
          href={`mailto:${supportEmail}`}>
          {supportEmail}
        </Link>
      </View>
      <FeedbackRequestAcknowledgement className="mt-auto pb-20" />
    </View>
  )
}
